import type { WebViewerInstance } from '@pdftron/webviewer';

type DocumentViewer = WebViewerInstance['Core']['documentViewer'];

/** The page origin in scroll coordinates, accounting for the embedded host. */
export function getPdfPageScrollTop(viewer: DocumentViewer, page: number): number | null {
  if (typeof viewer.getDisplayModeManager !== 'function') return null;
  const scroll = viewer.getScrollViewElement();
  const root = scroll.getRootNode();
  const hostTop = root instanceof ShadowRoot ? root.host.getBoundingClientRect().top : 0;
  const point = viewer.getDisplayModeManager().getDisplayMode().pageToWindow({ x: 0, y: 0 }, page);
  const top = point.y - (scroll.getBoundingClientRect().top - hostTop);
  return Number.isFinite(top) ? top : null;
}

/** Resolve the page occupying the most of the actual PDF viewport.
 * Apryse's mobile Web Component can emit page 2 then page 1 after scrolling
 * to page 2. Its public page geometry remains correct in that case.
 */
export function getVisiblePdfPage(viewer: DocumentViewer): number {
  const reported = viewer.getCurrentPage();
  const document = viewer.getDocument();
  if (!document) return reported;

  const scrollView = viewer.getScrollViewElement();
  const viewport = scrollView.getBoundingClientRect();
  if (viewport.width <= 0 || viewport.height <= 0) return reported;
  // pageToWindow uses content coordinates relative to the Web Component host
  // (or the iframe window), not browser client coordinates. Account for both
  // the embedded reader's position and its current scroll before intersecting.
  const root = scrollView.getRootNode();
  const hostRect = root instanceof ShadowRoot ? root.host.getBoundingClientRect() : null;
  const offsetX = (hostRect?.left ?? 0) - scrollView.scrollLeft;
  const offsetY = (hostRect?.top ?? 0) - scrollView.scrollTop;
  const mode = viewer.getDisplayModeManager().getDisplayMode();
  const pages = mode.getVisiblePages(0, 0);
  let bestPage = reported;
  let bestArea = 0;
  for (const page of pages) {
    const info = document.getPageInfo(page);
    const start = mode.pageToWindow({ x: 0, y: 0 }, page);
    const end = mode.pageToWindow({ x: info.width, y: info.height }, page);
    const width = Math.max(0, Math.min(viewport.right, Math.max(start.x, end.x) + offsetX)
      - Math.max(viewport.left, Math.min(start.x, end.x) + offsetX));
    const height = Math.max(0, Math.min(viewport.bottom, Math.max(start.y, end.y) + offsetY)
      - Math.max(viewport.top, Math.min(start.y, end.y) + offsetY));
    const area = width * height;
    if (area > bestArea || (area > 0 && area === bestArea && page === reported)) {
      bestPage = page;
      bestArea = area;
    }
  }
  return bestPage;
}
